import { memo, useMemo } from 'react';

import { type ShortUrlResponse } from '@air/api/types';
import { EllipsisVerticalIcon } from '@air/design-icons';
import { DropdownMenu, type DropdownMenuOption, renderDropdownItems } from '@air/primitive-dropdown-menu';
import { IconButton } from '@air/primitive-icon-button';

import { ShareLinkMenuFooter } from '~/components/ShareModal/components/ShareLink/ShareLinkMenuFooter';
import { NEW_TAB } from '~/constants/urls';

type ShareLinkProps = {
  createdAt?: ShortUrlResponse['createdAt'];
  createdByName?: string;
  shareUrl: string;
  menuActions?: DropdownMenuOption[];
};

export const ShareLink = memo(({ createdAt, createdByName, shareUrl, menuActions }: ShareLinkProps) => {
  const actionsToShow = useMemo(() => {
    const actions: DropdownMenuOption[] = [
      {
        id: 'preview',
        label: 'Preview',
        onSelect: () => window.open(shareUrl, NEW_TAB),
        type: 'item',
      },
    ];

    if (menuActions) {
      actions.push(...menuActions);
    }

    return actions;
  }, [menuActions, shareUrl]);

  return (
    <div className="flex h-8 flex-auto items-center justify-between rounded-8 border ads:border-input border-gray-5 border-solid ads:bg-input bg-gray-1 p-2">
      <p
        className="select-all truncate font-normal ads:text-primary text-14 text-gray-12"
        data-testid="PRIVATE_SHARE_LINK"
      >
        {shareUrl}
      </p>
      {!!menuActions?.length && (
        <DropdownMenu
          trigger={
            <IconButton
              adsIcon
              className="-mr-0.5"
              icon={EllipsisVerticalIcon}
              color="gray"
              appearance="ghost"
              label="Open menu"
              data-testid="SHARE_LINK_MENU_BUTTON"
            />
          }
        >
          {renderDropdownItems({ options: actionsToShow })}
          {(createdAt || createdByName) && <ShareLinkMenuFooter createdAt={createdAt} createdByName={createdByName} />}
        </DropdownMenu>
      )}
    </div>
  );
});

ShareLink.displayName = 'ShareLink';
